import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Delete,
  OpenInNew,
  Add,
} from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedSkill } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import UploadUniformModal from './UploadUniformModal';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'accepted':
      return <Badge label="Accepted" status="theme" />;
    case 'rejected':
      return <Badge label="Rejected" status="danger" />;
    case 'pending':
    default:
      return <Badge label="Pending" status="warning" />;
  }
};

const UniformsTab = ({ skill, workerId, onUpdate }: Props) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    uniformId: number | null;
    comment: string;
  }>({ open: false, uniformId: null, comment: '' });

  const uniforms = skill.skill_request?.uniforms || [];

  const handleAccept = async (uniformId: number) => {
    setProcessing(`accept-${uniformId}`);
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_uniforms/${uniformId}/accept`,
      );
      if (response.data.success) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error accepting uniform:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (uniformId: number) => {
    setRejectDialog({ open: true, uniformId, comment: '' });
  };

  const handleRejectConfirm = async () => {
    if (!rejectDialog.uniformId || !rejectDialog.comment) return;
    setProcessing(`reject-${rejectDialog.uniformId}`);
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_uniforms/${rejectDialog.uniformId}/reject`,
        { comment: rejectDialog.comment },
      );
      if (response.data.success) {
        setRejectDialog({ open: false, uniformId: null, comment: '' });
        onUpdate();
      }
    } catch (err) {
      console.error('Error rejecting uniform:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteClick = (uniformId: number) => {
    setDeletingId(uniformId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setProcessing(`delete-${deletingId}`);
    try {
      await axiosRubyClient.delete(`/api/admin/worker_uniforms/${deletingId}`);
      onUpdate();
    } catch (err) {
      console.error('Error deleting uniform:', err);
    } finally {
      setProcessing(null);
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  return (
    <>
      <Card>
        <Card.Section>
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <Body weight="semibold">Uniforms</Body>
              <Badge label={String(uniforms.length)} status="neutral" />
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload
            </Button>
          </Stack>
        </Card.Section>

        {uniforms.length === 0 ? (
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'center',
                padding: '20px',
              }}
            >
              <Small color="lighter">No uniforms uploaded yet</Small>
            </Stack>
          </Card.Section>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uniforms.map((uniform) => (
                  <TableRow key={uniform.id}>
                    <TableCell>{uniform.id}</TableCell>
                    <TableCell>{getStatusBadge(uniform.status)}</TableCell>
                    <TableCell>
                      {uniform.image_url ? (
                        <IconButton
                          size="small"
                          href={uniform.image_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View image"
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      ) : (
                        <Small color="lighter">--</Small>
                      )}
                    </TableCell>
                    <TableCell>
                      <Small>{uniform.comment || '--'}</Small>
                    </TableCell>
                    <TableCell>{formatDate(uniform.created_at)}</TableCell>
                    <TableCell>
                      <Stack gap={4}>
                        {uniform.status === 'pending' && (
                          <>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleAccept(uniform.id)}
                              disabled={processing !== null}
                              title="Accept"
                            >
                              {processing === `accept-${uniform.id}` ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckCircle fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRejectClick(uniform.id)}
                              disabled={processing !== null}
                              title="Reject"
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(uniform.id)}
                          disabled={processing !== null}
                          title="Delete"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() =>
          setRejectDialog({ open: false, uniformId: null, comment: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Uniform</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting this uniform.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={rejectDialog.comment}
            onChange={(e) =>
              setRejectDialog({ ...rejectDialog, comment: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setRejectDialog({ open: false, uniformId: null, comment: '' })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={!rejectDialog.comment || processing !== null}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingId(null);
        }}
      >
        <DialogTitle>Delete Uniform</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this uniform? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletingId(null);
            }}
            disabled={processing !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={processing !== null}
          >
            {processing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Modal */}
      <UploadUniformModal
        open={uploadModalOpen}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={onUpdate}
      />
    </>
  );
};

export default UniformsTab;
